import {
    Button,
    Divider,
    Flex,
    NumberInput,
    Text,
    TextInput,
    Title,
} from '@tremor/react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    ArrowRightStartOnRectangleIcon,
    BanknotesIcon,
    MinusIcon,
    PlusIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { useAuth } from '../../utilities/auth'
import {
    useApiV1BillingCheckoutCreate,
    useApiV1MeList,
    useApiV1MePaymentCreate,
    useApiV1BillingPromoUseCreate,
} from '../../api/api.gen'
import Spinner from '../Spinner'
import ContactUs from '../ContactUs'
import ManagedKubernetesTicketModal from '../../page/tickets/managed_kubernetes'
import OpenSearchTicketModal from '../../page/tickets/opensearch'
import PostgreSQLTicketModal from '../../page/tickets/postgres'
import MySQLTicketModal from '../../page/tickets/mysql'
import RedisTicketModal from '../../page/tickets/redis'
import Modal from './modal'
import OdooInstanceTicketModal from '../../page/tickets/odoo'
import LLMModelTicketModal from '../../page/tickets/llmmodel'
import { apiHostname, getAccountHeader } from '../../api/ApiConfig'
import { toErrorText } from '../../utilities/error'
import WatadLogo from '../../images/watad-large.png'

function AmountInput(props: any) {
    const { className } = props
    const newClassName = `border-0 ring-0 text-2xl text-center ${className}`
    return <input className={newClassName} {...props} />
}

function Profile() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const {
        response,
        isLoading,
        isExecuted,
        sendNow: refreshMe,
    } = useApiV1MeList()
    const [openCharge, setOpenCharge] = useState(false)
    const [amount, setAmount] = useState(1.0)
    const [promoCode, setPromoCode] = useState('')
    const [contactUsVisible, setContactUsVisible] = useState(false)
    const [companyAddress, setCompanyAddress] = useState('')
    const [companyName, setCompanyName] = useState('')
    const {
        response: checkoutResponse,
        isLoading: checkoutLoading,
        isExecuted: checkoutExecuted,
        error: checkoutError,
        sendNowWithParams: checkout,
    } = useApiV1BillingCheckoutCreate({}, {}, false)

    const {
        response: piResponse,
        isLoading: piLoading,
        isExecuted: piExecuted,
        error: piError,
        sendNow: setPaymentInfo,
    } = useApiV1MePaymentCreate(
        {
            companyAddress,
            companyName,
        },
        {},
        false
    )

    const {
        response: promoResponse,
        isLoading: promoLoading,
        isExecuted: promoExecuted,
        error: promoError,
        sendNowWithParams: applyPromo,
    } = useApiV1BillingPromoUseCreate({ code: '' }, {}, false)

    useEffect(() => {
        if (checkoutExecuted && !checkoutLoading) {
            window.localStorage.setItem(
                'checkout_session_id',
                (checkoutResponse?.sessionID || 0).toString()
            )
            window.open(checkoutResponse?.url || '', '_self')
        }
    }, [checkoutLoading])

    useEffect(() => {
        if (promoExecuted && !promoLoading && promoResponse?.applied) {
            // Refresh user info to reflect new balance
            try {
                refreshMe()
            } catch (e) {
                console.error(e)
            }
            alert(
                `Promo applied! New balance: ${(
                    promoResponse.newBalance || 0
                ).toFixed(3)} OMR`
            )
            setPromoCode('')
            setOpenCharge(false)
        }
    }, [promoExecuted, promoLoading])

    return (
        <Flex
            flexDirection="col"
            className="w-full border rounded-md p-2 space-y-4"
        >
            <Flex
                flexDirection="row"
                justifyContent="start"
                className="space-x-4 w-full"
            >
                {user.picture ? (
                    <img
                        src={user.picture}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover"
                    />
                ) : (
                    <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold bg-teal-500">
                        {user.name?.at(0)?.toUpperCase()}
                    </div>
                )}
                <Flex flexDirection="col" alignItems="start" className="w-fit">
                    <Text className="text-black font-bold text-lg">
                        {getAccountHeader()}
                    </Text>
                    <Text className="text-gray-900 font-normal text-lg">
                        {user.email}
                    </Text>
                    <Flex
                        flexDirection="row"
                        justifyContent="start"
                        className="space-x-1"
                    >
                        <Text className="text-gray-900 font-normal text-lg">
                            Balance:
                        </Text>
                        {isLoading && isExecuted ? (
                            <Spinner svgSize="w-4 h-4" />
                        ) : (
                            <span className="font-bold">
                                {response?.balance?.toFixed(3)} OMR
                            </span>
                        )}
                    </Flex>
                </Flex>
            </Flex>
            <Flex flexDirection="row">
                <Button
                    variant="secondary"
                    color="rose"
                    icon={ArrowRightStartOnRectangleIcon}
                    onClick={() => {
                        navigate('/logout')
                    }}
                >
                    Logout
                </Button>
                <Button
                    variant="secondary"
                    color="teal"
                    icon={BanknotesIcon}
                    onClick={() => setOpenCharge(true)}
                >
                    Charge
                </Button>
            </Flex>
            <ContactUs
                show={contactUsVisible}
                onClose={() => setContactUsVisible(false)}
            />
            <Modal
                title="Charge"
                show={openCharge}
                onCloseClicked={() => setOpenCharge(false)}
            >
                <Text className="mt-4 mb-8">
                    How much you would like to charge your account? (between
                    0.500 and 5000)
                </Text>
                <Flex
                    flexDirection="row"
                    className="w-full"
                    justifyContent="center"
                    alignItems="center"
                >
                    <NumericFormat
                        value={amount}
                        thousandSeparator=","
                        decimalScale={3}
                        fixedDecimalScale
                        suffix=" OMR"
                        customInput={AmountInput}
                        onValueChange={(v) => setAmount(v.floatValue || 0)}
                    />
                </Flex>
                <Flex
                    flexDirection="row"
                    justifyContent="center"
                    className="space-x-2"
                >
                    <Button
                        variant="secondary"
                        color="gray"
                        size="xs"
                        className="py-2 px-4"
                        icon={MinusIcon}
                        onClick={() => setAmount(amount - 1)}
                    />
                    <Button
                        variant="secondary"
                        color="gray"
                        size="xs"
                        className="py-2 px-4"
                        icon={PlusIcon}
                        onClick={() => setAmount(amount + 1)}
                    />
                </Flex>
                {response?.needPaymentInfo && (
                    <Flex flexDirection="col" className="my-4 space-y-4">
                        <Divider />
                        <Title>Billing Info</Title>
                        <Flex>
                            <Text>Company Name</Text>
                            <TextInput
                                value={companyName}
                                onValueChange={setCompanyName}
                            />
                        </Flex>
                        <Flex>
                            <Text>Company Address</Text>
                            <TextInput
                                value={companyAddress}
                                onValueChange={setCompanyAddress}
                            />
                        </Flex>
                        <Button
                            loading={piExecuted && piLoading}
                            disabled={piExecuted && piLoading}
                            color="teal"
                            onClick={setPaymentInfo}
                        >
                            Set Billing Info
                        </Button>
                        <Divider />
                    </Flex>
                )}
                {toErrorText(checkoutError)}
                <Button
                    loading={checkoutExecuted && checkoutLoading}
                    disabled={checkoutExecuted && checkoutLoading}
                    variant="secondary"
                    color="teal"
                    className="mt-12 w-full"
                    onClick={() => {
                        checkout(
                            {
                                baseURL: window.location.origin,
                                amountInBaisa: amount * 1000.0,
                            },
                            {}
                        )
                    }}
                >
                    Pay
                </Button>
                <Divider className="my-6" />
                <Title>Promo Code</Title>
                {toErrorText(promoError, 'Failed to apply promo code: ')}
                <Flex className="space-x-2 items-end">
                    <div className="flex-1">
                        <Text>Enter code</Text>
                        <TextInput
                            placeholder="e.g., WELCOME25"
                            value={promoCode}
                            onValueChange={setPromoCode}
                        />
                    </div>
                    <Button
                        variant="secondary"
                        color="teal"
                        loading={promoExecuted && promoLoading}
                        disabled={promoExecuted && promoLoading}
                        onClick={() => {
                            if (!promoCode || promoCode.trim().length === 0) {
                                return
                            }
                            applyPromo({ code: promoCode.trim() }, {})
                        }}
                    >
                        Apply
                    </Button>
                </Flex>
            </Modal>
        </Flex>
    )
}

export interface IModal {
    onCloseClicked: () => void
}

export default function Sidebar() {
    const { pathname, hash } = useLocation()
    const navigate = useNavigate()
    const { user } = useAuth()
    const [modalTitle, setModalTitle] = useState('')
    const [modal, setModal] = useState<React.ReactElement<IModal>>()

    const items = [
        {
            title: 'Virtual Machines',
            link: '/vm',
        },
        {
            title: 'Managed Kubernetes',
            modal: <ManagedKubernetesTicketModal />,
        },
        {
            title: 'OpenSearch',
            modal: <OpenSearchTicketModal />,
        },
        {
            title: 'PostgreSQL',
            modal: <PostgreSQLTicketModal />,
        },
        {
            title: 'MySQL',
            modal: <MySQLTicketModal />,
        },
        {
            title: 'Redis',
            modal: <RedisTicketModal />,
        },
        {
            title: 'Odoo Instance',
            link: '/odoo',
        },
        {
            title: 'LLM Model',
            modal: <LLMModelTicketModal />,
        },
        {
            title: 'Object Storage',
            link: '/s3',
        },
        {
            title: 'Access Management',
            link: '/sshkey',
        },
        {
            title: 'Role assignment',
            link: '/role',
        },
        {
            title: 'Pricing',
            link: '/pricing',
            colorful: true,
        },
        {
            title: 'Billing',
            link: '/billing',
            colorful: true,
        },
        {
            title: 'Support',
            link: '/tickets',
            colorful: true,
        },
    ]

    if (
        user.email === 'saleh.khazaei@gmail.com' ||
        user.email === 'h.abolhelm@gmail.com'
    ) {
        items.push({
            title: 'Administrator',
            link: '/admin',
        })
    }

    if (modal !== undefined && hash === '#close_sidebar_modal') {
        setModal(undefined)
        navigate(pathname)
    }

    return (
        <Flex
            flexDirection="col"
            justifyContent="between"
            className="min-w-72 w-fit h-full bg-gray-50 border pt-4 px-2 overflow-x-hidden"
        >
            <div className="w-48 mb-8">
                <img src={WatadLogo} alt="Watad Logo" />
            </div>{' '}
            <Flex
                flexDirection="col"
                justifyContent="start"
                className="w-full h-full p-0 m-0 space-y-2 overflow-y-auto"
            >
                {items.map((v) => {
                    let backgroundColor = 'bg-gray-100 hover:bg-gray-200'
                    if (pathname === v.link) {
                        backgroundColor = 'bg-gray-200'
                    }
                    if (v.colorful) {
                        if (pathname === v.link) {
                            backgroundColor = 'bg-teal-100 border-teal-200'
                        } else {
                            backgroundColor =
                                'bg-teal-50 hover:bg-teal-100 border-teal-200'
                        }
                    }

                    let textColor = 'font-normal text-gray-700'
                    if (pathname === v.link) {
                        textColor = 'font-extrabold text-teal-600'
                    }
                    if (v.colorful) {
                        if (pathname === v.link) {
                            textColor = 'font-extrabold text-teal-600'
                        } else {
                            textColor = 'font-normal text-teal-600'
                        }
                    }

                    return (
                        <Flex
                            className={`${backgroundColor} cursor-pointer border p-2 rounded-md`}
                            onClick={() => {
                                if (v.modal !== undefined) {
                                    setModal(v.modal)
                                    setModalTitle(v.title)
                                } else {
                                    navigate(v.link || '')
                                }
                            }}
                        >
                            <Text className={textColor}>{v.title}</Text>
                        </Flex>
                    )
                })}
            </Flex>
            <Flex flexDirection="col" justifyContent="end" className="pb-4">
                <Profile />
            </Flex>
            {modal !== undefined && (
                <Modal
                    title={modalTitle}
                    show={modal !== undefined}
                    onCloseClicked={() => setModal(undefined)}
                >
                    {modal}
                </Modal>
            )}
        </Flex>
    )
}
